import { StyleSheet, css } from 'aphrodite'
import type {
  BrokerInvoiceItem,
  BrokerInvoiceItemFields,
} from '../../types/invoice'
import type { Tag } from '../../types/tag'
import type { WorkOrderReportTag } from '../../types/workorder'

interface BrokerInvoiceItemsTableProps {
  items: BrokerInvoiceItem[]
  isEditable?: boolean
  onItemUpdate?: (
    index: number,
    field: BrokerInvoiceItemFields,
    value: string | number | undefined,
  ) => void
  onItemRemove?: (index: number) => void
  onAddItem?: () => void
  tags?: (Tag | WorkOrderReportTag)[]
  tagsFiltered?: (Tag | WorkOrderReportTag)[]
  contractorNames: Record<number, string>
  onObjectSelect?: (index: number, objectId: number) => void
  currentPage?: number
  totalPages?: number
  onPageChange?: (page: number) => void
  selectedTagIds?: number[]
}

const BrokerInvoiceItemsTable: React.FC<BrokerInvoiceItemsTableProps> = ({
  items,
  isEditable = false,
  onItemUpdate,
  onItemRemove,
  onAddItem,
  tags = [],
  tagsFiltered = [],
  contractorNames,
  onObjectSelect,
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  selectedTagIds = [],
}) => {
  const calculateItemTotal = (item: BrokerInvoiceItem) => {
    const parsed = Number.parseFloat(item.total || '0')
    return Number.isFinite(parsed) ? parsed : 0
  }

  const calculateTotal = () => {
    return items.reduce((sum, item) => sum + calculateItemTotal(item), 0)
  }

  const handleTagSelect = (index: number, value: string) => {
    onObjectSelect?.(index, Number(value))
  }

  return (
    <div className={css(styles.itemsTable)}>
      <table className={css(styles.table)}>
        <thead>
          <tr>
            <th className={css(styles.th, styles.tagHeader)}>Tag</th>
            <th className={css(styles.th, styles.jobSiteHeader)}>Job Site</th>
            <th className={css(styles.th, styles.hoursHeader)}>Hours</th>
            <th className={css(styles.th, styles.loadsHeader)}>Loads</th>
            <th className={css(styles.th, styles.dateHeader)}>Date</th>
            <th className={css(styles.th, styles.contractorHeader)}>
              Contractor
            </th>
            <th className={css(styles.th, styles.materialHeader)}>Material</th>
            <th className={css(styles.th, styles.numberHeader)}>Rate</th>
            <th className={css(styles.th, styles.numberHeader)}>Total</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => {
            const tag = tags.find(
              (t) => t.id?.toString() === item.tag_id?.toString(),
            )
            const selectedSet = new Set(
              (selectedTagIds || []).filter(
                (id) => typeof id === 'number' && id > 0,
              ),
            )
            const hasSelection =
              typeof item.tag_id === 'number' && (item.tag_id as number) > 0
            const optionSource = hasSelection ? tags : tagsFiltered
            const rowOptions = optionSource.filter((t) => {
              const id = Number(t.id)
              return id === item.tag_id || !selectedSet.has(id)
            })
            const formattedDate = item.date
              ? new Date(item.date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                })
              : undefined
            return (
              <tr key={item.id}>
                <td className={css(styles.td, styles.tagCell)}>
                  {isEditable ? (
                    <select
                      value={item.tag_id || -1}
                      onChange={(e) => handleTagSelect(index, e.target.value)}
                      className={css(styles.select)}
                    >
                      <option value={-1}>Select a tag</option>
                      {rowOptions.map((tag) => (
                        <option key={tag.id} value={tag.id}>
                          {tag.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    (() => {
                      const tag = tags.find((t) => t.id === item.tag_id)
                      return tag ? tag.label : '-'
                    })()
                  )}
                </td>
                <td className={css(styles.td, styles.jobSiteCell)}>
                  {isEditable ? (
                    <input
                      type="text"
                      value={item.job_site || ''}
                      onChange={(e) =>
                        onItemUpdate?.(index, 'job_site', e.target.value)
                      }
                      className={css(styles.tableInput)}
                      placeholder="Job Site"
                    />
                  ) : (
                    item.job_site || '-'
                  )}
                </td>
                <td className={css(styles.td, styles.hoursCell)}>
                  {isEditable ? (
                    <input
                      type="number"
                      value={item.hours || ''}
                      onChange={(e) =>
                        onItemUpdate?.(index, 'hours', e.target.value)
                      }
                      className={css(styles.tableInput)}
                      placeholder="Hours"
                    />
                  ) : (
                    item.hours || '-'
                  )}
                </td>
                <td className={css(styles.td, styles.loadsCell)}>
                  {isEditable ? (
                    <input
                      type="text"
                      value={item.number_of_loads || ''}
                      onChange={(e) =>
                        onItemUpdate?.(index, 'number_of_loads', e.target.value)
                      }
                      className={css(styles.tableInput)}
                      placeholder="Loads"
                    />
                  ) : (
                    item.number_of_loads || '-'
                  )}
                </td>
                <td className={css(styles.td, styles.dateCell)}>
                  {isEditable ? (
                    <input
                      type="date"
                      value={
                        item.date
                          ? new Date(item.date).toISOString().split('T')[0]
                          : ''
                      }
                      onChange={(e) =>
                        onItemUpdate?.(index, 'date', e.target.value)
                      }
                      className={css(styles.tableInput)}
                    />
                  ) : (
                    formattedDate || '-'
                  )}
                </td>
                <td className={css(styles.td, styles.contractorCell)}>
                  {(() => {
                    const contractorName = contractorNames[item.tag_id] || '-'
                    console.log(
                      `Looking up contractor for tag_id ${item.tag_id}:`,
                      contractorName,
                      'Available keys:',
                      Object.keys(contractorNames),
                    )
                    return contractorName
                  })()}
                </td>
                <td className={css(styles.td, styles.materialCell)}>
                  {isEditable ? (
                    <input
                      type="text"
                      value={item.material || ''}
                      onChange={(e) =>
                        onItemUpdate?.(index, 'material', e.target.value)
                      }
                      className={css(styles.tableInput)}
                      placeholder="Material"
                    />
                  ) : (
                    item.material || '-'
                  )}
                </td>
                <td className={css(styles.td, styles.numberCell)}>
                  {isEditable ? (
                    <input
                      type="number"
                      value={item.rate || ''}
                      onChange={(e) =>
                        onItemUpdate?.(index, 'rate', e.target.value)
                      }
                      className={css(styles.tableInput)}
                      placeholder="Rate"
                    />
                  ) : (
                    item.rate || '-'
                  )}
                </td>
                <td className={css(styles.td, styles.numberCell)}>
                  {isEditable ? (
                    <input
                      type="text"
                      value={item.total || ''}
                      onChange={(e) =>
                        onItemUpdate?.(index, 'total', e.target.value)
                      }
                      className={css(styles.tableInput)}
                      placeholder="Total"
                    />
                  ) : (
                    <span className={css(styles.amountText)}>
                      ${calculateItemTotal(item).toFixed(2)}
                    </span>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={7} className={css(styles.td)} />
            <td className={css(styles.td, styles.totalLabel)}>Total</td>
            <td
              className={css(styles.td, styles.numberCell, styles.totalAmount)}
            >
              ${calculateTotal().toFixed(2)}
            </td>
          </tr>
        </tfoot>
      </table>
      {isEditable && (
        <div className={css(styles.tableFooter)}>
          <button onClick={onAddItem} className={css(styles.addButton)}>
            <i className="fa-solid fa-plus" /> Add Item
          </button>
        </div>
      )}

      {totalPages > 1 && (
        <div className={css(styles.paginationControls)}>
          <button
            onClick={() => onPageChange?.(currentPage - 1)}
            disabled={currentPage <= 1}
            className={css(
              styles.paginationButton,
              currentPage <= 1 && styles.paginationButtonDisabled,
            )}
          >
            Previous
          </button>
          <span className={css(styles.paginationInfo)}>
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => onPageChange?.(currentPage + 1)}
            disabled={currentPage >= totalPages}
            className={css(
              styles.paginationButton,
              currentPage >= totalPages && styles.paginationButtonDisabled,
            )}
          >
            Next
          </button>
        </div>
      )}
    </div>
  )
}

const styles = StyleSheet.create({
  itemsTable: {
    marginBottom: '24px',
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    tableLayout: 'fixed',
    borderCollapse: 'collapse',
    marginBottom: '16px',
    fontSize: '12px',
    minWidth: 900,
  },
  th: {
    textAlign: 'left',
    padding: '12px',
    borderBottom: '2px solid #E5E7EB',
    fontSize: '12px',
    fontWeight: 600,
    color: '#374151',
  },
  td: {
    padding: '12px',
    borderBottom: '1px solid #E5E7EB',
    fontSize: '12px',
  },
  numberHeader: {
    width: '12%',
  },
  numberCell: {
    width: '12%',
  },
  tagHeader: {
    width: '15%',
  },
  tagCell: {
    width: '15%',
  },
  hoursHeader: {
    width: '8%',
  },
  hoursCell: {
    width: '8%',
  },
  tableInput: {
    width: '100%',
    border: '1px solid transparent',
    borderRadius: '4px',
    fontSize: '12px',
    '::placeholder': {
      fontSize: '11px',
      color: '#9CA3AF',
    },
    ':focus': {
      outline: 'none',
      borderColor: '#1C64F2',
      backgroundColor: '#F9FAFB',
    },
  },
  tableFooter: {
    display: 'flex',
    justifyContent: 'flex-start',
    marginTop: '16px',
  },
  addButton: {
    backgroundColor: '#1C64F2',
    color: 'white',
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: 500,
    height: 'fit-content',
    ':hover': {
      backgroundColor: '#1955CC',
    },
  },
  jobSiteHeader: {
    width: '20%',
  },
  jobSiteCell: {
    width: '20%',
  },
  amountText: {
    fontSize: '12px',
    color: '#374151',
  },
  select: {
    width: '100%',
    border: '1px solid transparent',
    borderRadius: '4px',
    fontSize: '12px',
    backgroundColor: 'white',
    ':focus': {
      outline: 'none',
      borderColor: '#1C64F2',
      backgroundColor: '#F9FAFB',
    },
  },
  totalLabel: {
    fontWeight: 600,
    color: '#111827',
    fontSize: '18px',
  },
  totalAmount: {
    fontWeight: 600,
    color: '#111827',
    fontSize: '18px',
  },

  dateHeader: {
    textAlign: 'center',
    width: '12%',
  },
  contractorHeader: {
    width: '18%',
  },
  contractorCell: {
    width: '18%',
  },
  materialHeader: {
    width: '15%',
  },
  materialCell: {
    width: '15%',
  },
  loadsHeader: {
    width: '8%',
    textAlign: 'center',
  },
  loadsCell: {
    width: '8%',
    textAlign: 'center',
  },
  dateCell: {
    width: '12%',
    textAlign: 'center',
  },
  paginationControls: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '16px',
    marginTop: '16px',
    padding: '12px 0',
  },
  paginationButton: {
    backgroundColor: '#1C64F2',
    color: 'white',
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    ':hover': {
      backgroundColor: '#1955CC',
    },
  },
  paginationButtonDisabled: {
    backgroundColor: '#D1D5DB',
    color: '#9CA3AF',
    cursor: 'not-allowed',
    ':hover': {
      backgroundColor: '#D1D5DB',
    },
  },
  paginationInfo: {
    fontSize: '14px',
    color: '#6B7280',
    fontWeight: 500,
  },
})

export default BrokerInvoiceItemsTable
